/**
 * THE SPREAD — one bar at the typical value, with the range drawn ON it.
 *
 * ── THE PROBLEM THIS SOLVES ──
 *
 * The dwell panel prints p50, p90 and p99 as three separate figures. Three
 * numbers side by side are three numbers: nothing about "17.2, 50, 111" says
 * that the middle half of the distribution is tight and the tail is enormous,
 * which is the actual finding, and which the same three numbers make obvious the
 * moment they are drawn on one line.
 *
 * `ErrorBar` puts the range on the SAME MARK as the value. That is its whole
 * point and it is why it is used here rather than a second series: a separate
 * "p90 line" would be a second thing to compare against the first, and the
 * reader would have to hold both. One bar with whiskers is one thing.
 *
 * ── AND IT IS EXPLICITLY NOT A CONFIDENCE INTERVAL ──
 *
 * ErrorBar's usual meaning is statistical uncertainty about an estimate. This is
 * not that: p50 and p90 are exact facts about the observed set, not estimates
 * with error. The caption says which, because a reader who has seen error bars
 * before will assume the other one.
 */
import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ErrorBar,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { dataViz, palette } from '../../../../../style-utils/theme';
import { TipCard, TipLabel, TipNote, TipRow, TipStrong } from './chart.styled';
import { BAR_RADIUS, axisCommon, cursor, grid, margins } from './chart-theme';

export interface SpreadRow {
  key: string;
  label: string;
  /** The bar's own height — the typical value. */
  value: number;
  /** The whisker, as [below, above] the value. Recharts takes it that way. */
  range: [number, number];
  /** Everything the tooltip needs to state the three raw figures. */
  p50: number | null;
  p90: number | null;
  p99: number | null;
  unit: string;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
const SpreadTip: React.FC<any> = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload as SpreadRow;

  return (
    <TipCard role="tooltip">
      <TipLabel>{row.label}</TipLabel>
      <TipRow>
        <TipStrong>{row.p50?.toLocaleString() ?? '—'}</TipStrong>
        <span>{row.unit} — half waited less than this</span>
      </TipRow>
      <TipRow>
        <TipStrong>{row.p90?.toLocaleString() ?? '—'}</TipStrong>
        <span>{row.unit} — nine in ten waited less than this</span>
      </TipRow>
      <TipRow>
        <TipStrong>{row.p99?.toLocaleString() ?? '—'}</TipStrong>
        <span>{row.unit} — the worst hundredth</span>
      </TipRow>
      <TipNote>
        The whisker is the observed p50-to-p90 range, not a margin of error. These
        are exact facts about what happened, not estimates.
      </TipNote>
    </TipCard>
  );
};
/* eslint-enable @typescript-eslint/no-explicit-any */

export const SpreadBar: React.FC<{ rows: SpreadRow[]; threshold?: number | null }> = ({
  rows,
}) => (
  <BarChart accessibilityLayer data={rows} layout="vertical" margin={margins.default}>
    <CartesianGrid {...grid} vertical horizontal={false} />
    <XAxis type="number" {...axisCommon} allowDecimals />
    <YAxis type="category" dataKey="label" {...axisCommon} width={92} />
    <Tooltip content={<SpreadTip />} cursor={cursor.bar} />
    <Bar dataKey="value" fill={dataViz.series[0]} radius={BAR_RADIUS} barSize={22}>
      <ErrorBar
        dataKey="range"
        direction="x"
        width={6}
        strokeWidth={2}
        stroke={palette.inkSoft}
      />
    </Bar>
  </BarChart>
);
